import { X } from "lucide-react";
import styles from "./ProductDetails.module.scss";
import { formatNumberToPrice } from "../../../../helpers/priceFormatting";
import type { SalesProduct } from "../ProductsTable/types";

interface ProductDetailsPanelProps {
  product: SalesProduct | null;
  isOpen: boolean;
  onClose: () => void;
}

export function ProductDetails({
  product,
  isOpen,
  onClose,
}: ProductDetailsPanelProps) {
  if (!isOpen || !product) return null;

  return (
    <div className={styles.detailsBackdrop} onClick={onClose}>
      <div
        className={styles.detailsPanel}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Detalles del producto ${product.name}`}
      >
        <div className={styles.detailsHeader}>
          <div className={styles.detailsTitleGroup}>
            <h3 className={styles.detailsTitle}>{product.name}</h3>
            <span className={styles.detailsSubtitle}>ID: {product.id}</span>
          </div>
          <button
            className={styles.closeButton}
            aria-label="Cerrar detalles"
            onClick={onClose}
          >
            <X className={styles.closeIcon} />
          </button>
        </div>
        <div className={styles.detailsContent}>
          {product.principal_image && product.principal_image.length > 0 && (
            <div className={styles.imagePreview}>
              <img
                src={product.principal_image[0].url}
                alt={product.principal_image[0].name || product.name}
              />
            </div>
          )}
          {product.description && (
            <div className={styles.detailsBlock}>
              <span className={styles.detailsLabel}>Descripción</span>
              <p className={styles.detailsParagraph}>{product.description}</p>
            </div>
          )}

          <div className={styles.detailsRow}>
            <span className={styles.detailsLabel}>Categoría</span>
            <span className={styles.detailsValue}>{product.category}</span>
          </div>
          <div className={styles.detailsRow}>
            <span className={styles.detailsLabel}>Línea</span>
            <span className={styles.detailsValue}>{product.brand}</span>
          </div>
          {(product.color || product.color_hex_code) && (
            <div className={styles.detailsRow}>
              <span className={styles.detailsLabel}>Color</span>
              <span className={styles.detailsValue}>
                {product.color}
                {product.color_hex_code && (
                  <span
                    className={styles.colorSwatch}
                    style={{ backgroundColor: product.color_hex_code }}
                    aria-label={`Color ${product.color}`}
                  />
                )}
              </span>
            </div>
          )}
          {product.material && (
            <div className={styles.detailsRow}>
              <span className={styles.detailsLabel}>Material</span>
              <span className={styles.detailsValue}>{product.material}</span>
            </div>
          )}

          {product.line && (
            <div className={styles.detailsRow}>
              <span className={styles.detailsLabel}>Línea</span>
              <span className={styles.detailsValue}>{product.line}</span>
            </div>
          )}

          <div className={styles.detailsRow}>
            <span className={styles.detailsLabel}>Disponibles</span>
            <span className={styles.detailsValue}>
              {product.units_available}
            </span>
          </div>
          {(product.min_sale_unit || product.units_per_package) && (
            <div className={styles.detailsRow}>
              <span className={styles.detailsLabel}>
                Unidad mínima / por paquete
              </span>
              <span className={styles.detailsValue}>
                {product.min_sale_unit ?? "-"} /{" "}
                {product.units_per_package ?? "-"}
              </span>
            </div>
          )}
          <div className={styles.detailsRow}>
            <span className={styles.detailsLabel}>Precio</span>
            <span className={styles.detailsValue}>
              {formatNumberToPrice(product.price)}
            </span>
          </div>
          <div className={styles.detailsRow}>
            <span className={styles.detailsLabel}>Precio con IVA</span>
            <span className={styles.detailsValue}>
              {formatNumberToPrice(product.taxed_price)}
            </span>
          </div>
          {product.price_per_m2 && (
            <div className={styles.detailsRow}>
              <span className={styles.detailsLabel}>Precio por m2</span>
              <span className={styles.detailsValue}>
                {formatNumberToPrice(product.price_per_m2)}
              </span>
            </div>
          )}
          {typeof product.is_with_iva !== "undefined" && (
            <div className={styles.detailsRow}>
              <span className={styles.detailsLabel}>Incluye IVA</span>
              <span className={styles.detailsValue}>
                {product.is_with_iva ? "Sí" : "No"}
              </span>
            </div>
          )}
          {(product.comission_percentage || product.delivery_time) && (
            <div className={styles.detailsRow}>
              <span className={styles.detailsLabel}>
                Comisión / Entrega (días)
              </span>
              <span className={styles.detailsValue}>
                {product.comission_percentage ?? "-"}% /{" "}
                {product.delivery_time ?? "-"}
              </span>
            </div>
          )}
          {typeof product.warranty !== "undefined" && (
            <div className={styles.detailsRow}>
              <span className={styles.detailsLabel}>Garantía</span>
              <span className={styles.detailsValue}>
                {product.warranty}{" "}
                {product.is_warranty_in_months ? "meses" : "años"}
              </span>
            </div>
          )}
          {(product.presentation || product.unit_of_measure) && (
            <div className={styles.detailsRow}>
              <span className={styles.detailsLabel}>Presentación / Unidad</span>
              <span className={styles.detailsValue}>
                {product.presentation ?? "-"} / {product.unit_of_measure ?? "-"}
              </span>
            </div>
          )}
          <div className={styles.detailsRow}>
            <span className={styles.detailsLabel}>Dimensiones</span>
            <span className={styles.detailsValue}>
              {product.width ?? "-"}m × {product.length ?? "-"}m
              {product.height ? ` × ${product.height}m` : ""}
              {product.depth ? ` × profundidad ${product.depth}m` : ""}
              {product.diameter ? ` × diámetro ${product.diameter}m` : ""}
            </span>
          </div>
          {(product.packing_length ||
            product.packing_width ||
            product.packing_height ||
            product.packing_weight) && (
            <div className={styles.detailsRow}>
              <span className={styles.detailsLabel}>
                Empaque (Largo×Ancho×Alto, peso)
              </span>
              <span className={styles.detailsValue}>
                {product.packing_length ?? "-"} × {product.packing_width ?? "-"}{" "}
                × {product.packing_height ?? "-"} m,{" "}
                {product.packing_weight ?? "-"} kg
              </span>
            </div>
          )}
          {product.technical_sheet && product.technical_sheet.length > 0 && (
            <div className={styles.detailsBlock}>
              <span className={styles.detailsLabel}>Ficha técnica</span>
              <div className={styles.sheetList}>
                {product.technical_sheet.map((doc) => (
                  <a
                    key={doc.id}
                    className={styles.sheetLink}
                    href={doc.url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {doc.name}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
